import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  DAYS_OF_WEEK,
  getStoreHoursStatus,
  hasEnabledStoreHours,
} from '../../StoreHoursPage/StoreHoursForm/storeHoursUtils';

import css from './StoreHoursRow.module.css';

/** Localized 12h time from `HH:mm` (store hours storage format). */
const formatTime24WithIntl = (intl, time24) => {
  if (!time24) return '';
  const [hRaw, mRaw] = time24.split(':');
  const h = parseInt(hRaw, 10);
  const m = parseInt(mRaw, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return time24;
  const d = new Date(2000, 0, 1, h, m, 0, 0);
  return intl.formatTime(d, { hour: 'numeric', minute: '2-digit', hour12: true });
};

/**
 * Subtle "is this store open now" row for the Store Header (mobile & desktop), shown
 * directly under the location/map section. Collapsed by default; clicking/tapping expands
 * the same weekly schedule shown elsewhere (StoreHoursCard, ListingPage's StoreHoursMaybe).
 * Renders nothing when the owner hasn't configured any store hours.
 */
const StoreHoursRow = ({ storeHours, timeZone }) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  const status = useMemo(() => getStoreHoursStatus(storeHours, undefined, timeZone), [
    storeHours,
    timeZone,
  ]);

  if (!hasEnabledStoreHours(storeHours)) {
    return null;
  }

  const todayKey = status.todayKey;

  const weekdayLabel = dayKey => {
    const day = DAYS_OF_WEEK.find(d => d.key === dayKey);
    return intl.formatMessage({
      id: `StorefrontTabContent.storeHours.weekday.${dayKey}`,
      defaultMessage: day?.label || dayKey,
    });
  };

  const todayHoursText = status.todayOpenSlots
    .map(
      slot =>
        `${formatTime24WithIntl(intl, slot.openTime)} - ${formatTime24WithIntl(
          intl,
          slot.closeTime
        )}`
    )
    .join(', ');

  const summaryMessage = status.isOpen ? (
    <FormattedMessage
      id="StoreProfilePage.storeHours.status.todaySummary"
      defaultMessage="Today {hours}"
      values={{ hours: todayHoursText }}
    />
  ) : status.nextOpenTime && status.nextOpenIsToday ? (
    <FormattedMessage
      id="StorefrontTabContent.storeHours.status.opensToday"
      defaultMessage="Opens today at {time}"
      values={{ time: formatTime24WithIntl(intl, status.nextOpenTime) }}
    />
  ) : status.nextOpenTime ? (
    <FormattedMessage
      id="StorefrontTabContent.storeHours.status.opensOnDay"
      defaultMessage="Opens {weekday} at {time}"
      values={{
        weekday: weekdayLabel(status.nextOpenDayKey),
        time: formatTime24WithIntl(intl, status.nextOpenTime),
      }}
    />
  ) : null;

  return (
    <div className={css.root}>
      <button
        type="button"
        className={css.row}
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
        aria-controls="store-header-hours-list"
      >
        <span className={css.statusBadge}>
          <span
            className={classNames(css.statusDot, {
              [css.statusOpen]: status.isOpen,
              [css.statusClosed]: !status.isOpen,
            })}
            aria-hidden="true"
          />
          <span
            className={classNames({
              [css.statusOpen]: status.isOpen,
              [css.statusClosed]: !status.isOpen,
            })}
          >
            {status.isOpen ? (
              <FormattedMessage id="StorefrontTabContent.storeHours.status.open" defaultMessage="Open" />
            ) : (
              <FormattedMessage
                id="StorefrontTabContent.storeHours.status.closed"
                defaultMessage="Closed"
              />
            )}
          </span>
          {summaryMessage ? <span className={css.summary}>&middot; {summaryMessage}</span> : null}
        </span>
        <span
          className={classNames(css.chevron, {
            [css.chevronExpanded]: isExpanded,
          })}
          aria-hidden
        >
          <i className="ri-arrow-down-s-line" />
        </span>
      </button>
      <div
        id="store-header-hours-list"
        className={classNames(css.expandWrapper, {
          [css.expandWrapperOpen]: isExpanded,
        })}
      >
        <div className={css.weekList}>
          {DAYS_OF_WEEK.map(day => {
            const dayData = storeHours[day.key];
            const isEnabled = dayData?.enabled;
            const timeSlots = dayData?.timeSlots || [];
            const isToday = day.key === todayKey;

            return (
              <div key={day.key} className={css.weekRow}>
                <span className={css.weekDay}>
                  {intl.formatMessage({
                    id: `StorefrontTabContent.storeHours.weekday.${day.key}`,
                    defaultMessage: day.label,
                  })}
                </span>
                <div className={css.weekTimeContainer}>
                  {isEnabled && timeSlots.length > 0 ? (
                    timeSlots.map((timeSlot, index) => (
                      <span
                        key={index}
                        className={classNames(css.weekTime, isToday ? css.weekTimeToday : null)}
                      >
                        {timeSlot?.openTime && timeSlot?.closeTime
                          ? `${formatTime24WithIntl(
                              intl,
                              timeSlot.openTime
                            )} - ${formatTime24WithIntl(intl, timeSlot.closeTime)}`
                          : intl.formatMessage({
                              id: 'StorefrontTabContent.storeHours.closed',
                              defaultMessage: 'Closed',
                            })}
                      </span>
                    ))
                  ) : (
                    <span className={classNames(css.weekTime, isToday ? css.weekTimeToday : null)}>
                      <FormattedMessage
                        id="StorefrontTabContent.storeHours.closed"
                        defaultMessage="Closed"
                      />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className={css.onlineBooking}>
          <FormattedMessage
            id="StorefrontTabContent.storeHours.onlineBooking"
            defaultMessage="Online booking available 24/7"
          />
        </p>
      </div>
    </div>
  );
};

StoreHoursRow.propTypes = {
  storeHours: PropTypes.object,
  // IANA timezone of the store's own location, so open/closed status is evaluated there
  // instead of the viewer's or server's local time (see storeHoursUtils#getStoreTimeZone).
  timeZone: PropTypes.string,
};

export default StoreHoursRow;
